import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  message,
  Progress,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { DownloadOutlined, DownOutlined, EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";

import {
  clientImportApi,
  skuImportApi,
  articleSeasonImportApi,
  paymentMethodImportApi,
  promotionImportApi,
  pointsMovementImportApi,
  branchOfficeImportApi,
  purchaseOrderImportApi,
  repositionOrderImportApi,
} from "../../api/importClients";
import { createJsonlImportClient } from "../../api/jsonlImportApi";
import type { JsonlDownloadLink, JsonlImportRecord } from "../../api/jsonlImportApi";
import JsonlViewerModal from "../../components/jsonlviewer/JsonlViewerModal";
import type { JsonlFetchPage } from "../../components/jsonlviewer/jsonlUtils";
import {
  downloadOriginalPricingFile,
  downloadOriginalRewardFile,
  downloadOriginalStockMovementFile,
  downloadOriginalTicketFile,
  downloadPricingImportErrors,
  downloadRewardImportErrors,
  downloadStockMovementImportErrors,
  downloadTicketImportErrors,
  getPricingImports,
  getRewardImports,
  getStockMovementImports,
  getTicketImports,
} from "../../api/Services";
import { privilegesEnum } from "../../helpers/PermissionHelper";
import { MENU_VISIBILITY_CONFIG } from "../../config/menuVisibility";
import { BusinessUniteContext } from "../../components/layout/Layout";
import styles from "./ImportDashboard.module.scss";

const { Title, Text } = Typography;
const RECENT_PER_SOURCE = 50;
const numberFormatter = new Intl.NumberFormat("es-UY");
const legacyTicketImportApi = createJsonlImportClient("/api/tickets");
const legacyRewardImportApi = createJsonlImportClient("/api/rewards");
const legacyStockMovementImportApi = createJsonlImportClient("/api/stockMovements");
const legacyPricingImportApi = createJsonlImportClient("/api/pricings");

type ImportStatus = "PENDING" | "PROCESSING" | "COMPLETED" | "ERROR" | "CANCELLED";

interface ImportListPage {
  content: JsonlImportRecord[];
  totalElements: number;
}

interface ImportSource {
  key: string;
  titleKey: string;
  route: string;
  enabled: boolean;
  permission?: privilegesEnum | privilegesEnum[];
  load: () => Promise<unknown>;
  downloadOriginal: (processId: string) => Promise<JsonlDownloadLink>;
  downloadErrors: (processId: string) => Promise<JsonlDownloadLink>;
  preview: (
    processId: string,
    page: number,
    size: number,
    search?: string
  ) => Promise<unknown>;
}

interface ImportSourceSummary {
  key: string;
  titleKey: string;
  route: string;
  records: JsonlImportRecord[];
  totalElements: number;
  failed: boolean;
  downloadOriginal: ImportSource["downloadOriginal"];
  downloadErrors: ImportSource["downloadErrors"];
  preview: ImportSource["preview"];
}

interface ImportDashboardRow extends JsonlImportRecord {
  sourceKey: string;
  sourceTitleKey: string;
  sourceRoute: string;
  rowKey: string;
}

const sources: ImportSource[] = [
  {
    key: "tickets",
    titleKey: "ImportDashboard-SourceTickets",
    route: "/admin/tickets/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.ticketImport,
    permission: privilegesEnum.WRITE_TICKETS,
    load: () => getTicketImports(0, RECENT_PER_SOURCE),
    downloadOriginal: downloadOriginalTicketFile,
    downloadErrors: downloadTicketImportErrors,
    preview: legacyTicketImportApi.preview,
  },
  {
    key: "rewards",
    titleKey: "ImportDashboard-SourceRewards",
    route: "/admin/rewards/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.rewardImport,
    permission: privilegesEnum.READ_REWARDS,
    load: () => getRewardImports(0, RECENT_PER_SOURCE),
    downloadOriginal: downloadOriginalRewardFile,
    downloadErrors: downloadRewardImportErrors,
    preview: legacyRewardImportApi.preview,
  },
  {
    key: "stockMovements",
    titleKey: "ImportDashboard-SourceStockMovements",
    route: "/admin/stockmovements/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.stockMovementImport,
    permission: [
      privilegesEnum.READ_STOCK_MOVEMENTS,
      privilegesEnum.WRITE_STOCK_MOVEMENTS,
      privilegesEnum.DELETE_STOCK_MOVEMENTS,
    ],
    load: () => getStockMovementImports(0, RECENT_PER_SOURCE),
    downloadOriginal: downloadOriginalStockMovementFile,
    downloadErrors: downloadStockMovementImportErrors,
    preview: legacyStockMovementImportApi.preview,
  },
  {
    key: "pricings",
    titleKey: "ImportDashboard-SourcePricings",
    route: "/admin/pricings/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.pricingImport,
    permission: privilegesEnum.READ_PRICINGS,
    load: () => getPricingImports(0, RECENT_PER_SOURCE),
    downloadOriginal: downloadOriginalPricingFile,
    downloadErrors: downloadPricingImportErrors,
    preview: legacyPricingImportApi.preview,
  },
  {
    key: "clients",
    titleKey: "ImportDashboard-SourceClients",
    route: "/admin/clients/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.clientImport,
    permission: privilegesEnum.WRITE_CLIENTS,
    load: () => clientImportApi.list(0, RECENT_PER_SOURCE),
    downloadOriginal: clientImportApi.originalUrl,
    downloadErrors: clientImportApi.errorsUrl,
    preview: clientImportApi.preview,
  },
  {
    key: "skus",
    titleKey: "ImportDashboard-SourceSkus",
    route: "/admin/articles/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.skuImport,
    permission: privilegesEnum.WRITE_ARTICLES,
    load: () => skuImportApi.list(0, RECENT_PER_SOURCE),
    downloadOriginal: skuImportApi.originalUrl,
    downloadErrors: skuImportApi.errorsUrl,
    preview: skuImportApi.preview,
  },
  {
    key: "articleSeasons",
    titleKey: "ImportDashboard-SourceArticleSeasons",
    route: "/admin/articles/seasons/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.articleSeasonImport,
    permission: privilegesEnum.WRITE_ARTICLES,
    load: () => articleSeasonImportApi.list(0, RECENT_PER_SOURCE),
    downloadOriginal: articleSeasonImportApi.originalUrl,
    downloadErrors: articleSeasonImportApi.errorsUrl,
    preview: articleSeasonImportApi.preview,
  },
  {
    key: "paymentMethods",
    titleKey: "ImportDashboard-SourcePaymentMethods",
    route: "/admin/acceptedpaymentmethods/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.paymentMethodImport,
    permission: privilegesEnum.READ_ACCEPTED_PAYMENT_METHODS,
    load: () => paymentMethodImportApi.list(0, RECENT_PER_SOURCE),
    downloadOriginal: paymentMethodImportApi.originalUrl,
    downloadErrors: paymentMethodImportApi.errorsUrl,
    preview: paymentMethodImportApi.preview,
  },
  {
    key: "promotions",
    titleKey: "ImportDashboard-SourcePromotions",
    route: "/admin/promotions/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.promotionImport,
    permission: privilegesEnum.WRITE_PROMOTIONS,
    load: () => promotionImportApi.list(0, RECENT_PER_SOURCE),
    downloadOriginal: promotionImportApi.originalUrl,
    downloadErrors: promotionImportApi.errorsUrl,
    preview: promotionImportApi.preview,
  },
  {
    key: "pointsMovements",
    titleKey: "ImportDashboard-SourcePointsMovements",
    route: "/admin/clients/points-movements/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.pointsMovementImport,
    permission: privilegesEnum.WRITE_CLIENTS,
    load: () => pointsMovementImportApi.list(0, RECENT_PER_SOURCE),
    downloadOriginal: pointsMovementImportApi.originalUrl,
    downloadErrors: pointsMovementImportApi.errorsUrl,
    preview: pointsMovementImportApi.preview,
  },
  {
    key: "branchOffices",
    titleKey: "ImportDashboard-SourceBranchOffices",
    route: "/admin/branchoffices/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.branchOfficeImport,
    permission: privilegesEnum.READ_BRANCH_OFFICES,
    load: () => branchOfficeImportApi.list(0, RECENT_PER_SOURCE),
    downloadOriginal: branchOfficeImportApi.originalUrl,
    downloadErrors: branchOfficeImportApi.errorsUrl,
    preview: branchOfficeImportApi.preview,
  },
  {
    key: "purchaseOrders",
    titleKey: "ImportDashboard-SourcePurchaseOrders",
    route: "/admin/purchaseorders/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.purchaseOrderImport,
    permission: privilegesEnum.WRITE_PURCHASE_ORDERS,
    load: () => purchaseOrderImportApi.list(0, RECENT_PER_SOURCE),
    downloadOriginal: purchaseOrderImportApi.originalUrl,
    downloadErrors: purchaseOrderImportApi.errorsUrl,
    preview: purchaseOrderImportApi.preview,
  },
  {
    key: "repositionOrders",
    titleKey: "ImportDashboard-SourceRepositionOrders",
    route: "/admin/repositionorders/import",
    enabled: MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.repositionOrderImport,
    permission: privilegesEnum.WRITE_REPOSITION_ORDERS,
    load: () => repositionOrderImportApi.list(0, RECENT_PER_SOURCE),
    downloadOriginal: repositionOrderImportApi.originalUrl,
    downloadErrors: repositionOrderImportApi.errorsUrl,
    preview: repositionOrderImportApi.preview,
  },
];

const canSeeSource = (source: ImportSource, privilegesSnapshot: string | null) => {
  if (!source.enabled) return false;
  if (!source.permission) return true;
  try {
    const permissions = Array.isArray(source.permission)
      ? source.permission
      : [source.permission];
    const privileges = JSON.parse(privilegesSnapshot || "[]");
    return permissions.some((permission) =>
      privileges.some((privilege: { name: string }) => privilege.name === permission)
    );
  } catch {
    return false;
  }
};

const unwrapImportList = (value: unknown): ImportListPage => {
  const response = value as any;
  const page = response?.data?.data ?? response?.data ?? response;
  const content = Array.isArray(page?.content) ? page.content : [];
  return {
    content,
    totalElements: Number(page?.totalElements ?? content.length),
  };
};

const formatNumber = (value: number) => numberFormatter.format(value);
const readCount = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const getStatusLabelKey = (status?: string) => {
  switch (status) {
    case "COMPLETED":
      return "ImportDashboard-StatusCompleted";
    case "PROCESSING":
      return "ImportDashboard-StatusProcessing";
    case "ERROR":
      return "ImportDashboard-StatusError";
    case "CANCELLED":
      return "ImportDashboard-StatusCancelled";
    default:
      return "ImportDashboard-StatusPending";
  }
};

const statusColor = (status?: string) => {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "PROCESSING":
      return "processing";
    case "ERROR":
      return "error";
    case "CANCELLED":
      return "warning";
    default:
      return "default";
  }
};

const getRecordDate = (record: JsonlImportRecord) =>
  record.startedAt || record.finishedAt || undefined;

const formatDate = (value?: string) =>
  value && moment(value).isValid() ? moment(value).format("DD/MM/YYYY HH:mm") : "—";

const ImportDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contextValue: businessUnitSelected } = useContext(BusinessUniteContext);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summaries, setSummaries] = useState<ImportSourceSummary[]>([]);
  const [expandedSources, setExpandedSources] = useState<Record<string, boolean>>({});
  const [downloadingFiles, setDownloadingFiles] = useState<Record<string, boolean>>({});
  const [viewerSelection, setViewerSelection] = useState<{
    summary: ImportSourceSummary;
    record: JsonlImportRecord;
  } | null>(null);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<string>();

  const privilegesSnapshot = localStorage.getItem("privileges");
  const visibleSources = useMemo(
    () => sources.filter((source) => canSeeSource(source, privilegesSnapshot)),
    [privilegesSnapshot]
  );

  const loadDashboard = useCallback(
    async (showSkeleton = false) => {
      if (showSkeleton) setLoading(true);
      else setRefreshing(true);

      const results = await Promise.all(
        visibleSources.map(async (source): Promise<ImportSourceSummary> => {
          const sourceSummary = {
            key: source.key,
            titleKey: source.titleKey,
            route: source.route,
            downloadOriginal: source.downloadOriginal,
            downloadErrors: source.downloadErrors,
            preview: source.preview,
          };
          try {
            const page = unwrapImportList(await source.load());
            return {
              ...sourceSummary,
              records: page.content,
              totalElements: page.totalElements,
              failed: false,
            };
          } catch {
            return {
              ...sourceSummary,
              records: [],
              totalElements: 0,
              failed: true,
            };
          }
        })
      );

      setSummaries(results);
      setLoading(false);
      setRefreshing(false);
    },
    [visibleSources]
  );

  useEffect(() => {
    if (!businessUnitSelected) {
      setLoading(false);
      return;
    }
    void loadDashboard(true);
  }, [businessUnitSelected, loadDashboard]);

  const recentRows = useMemo<ImportDashboardRow[]>(
    () =>
      summaries
        .flatMap((summary) =>
          summary.records.map((record) => ({
            ...record,
            sourceKey: summary.key,
            sourceTitleKey: summary.titleKey,
            sourceRoute: summary.route,
            rowKey: `${summary.key}-${record.id}`,
          }))
        )
        .sort((left, right) => {
          const leftTime = getRecordDate(left);
          const rightTime = getRecordDate(right);
          return (
            (rightTime ? moment(rightTime).valueOf() : 0) -
            (leftTime ? moment(leftTime).valueOf() : 0)
          );
        }),
    [summaries]
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return recentRows.filter((record) => {
      if (sourceFilter && record.sourceKey !== sourceFilter) return false;
      if (statusFilter && record.status !== statusFilter) return false;
      if (normalizedSearch && !record.originalFilename?.toLowerCase().includes(normalizedSearch)) {
        return false;
      }
      return true;
    });
  }, [recentRows, search, sourceFilter, statusFilter]);

  const viewerFetchPage = useMemo<JsonlFetchPage | null>(() => {
    if (!viewerSelection) return null;
    return async ({ page, size, search: previewSearch }) =>
      viewerSelection.summary.preview(
        viewerSelection.record.id,
        page,
        size,
        previewSearch
      ) as ReturnType<JsonlFetchPage>;
  }, [viewerSelection]);

  const failedSourcesCount = summaries.filter((summary) => summary.failed).length;

  const downloadImportFile = async (
    kind: "original" | "errors",
    summary: ImportSourceSummary,
    record: JsonlImportRecord
  ) => {
    const key = `${kind}-${record.id}`;
    if (downloadingFiles[key]) return;
    setDownloadingFiles((previous) => ({ ...previous, [key]: true }));

    try {
      const getLink = kind === "original" ? summary.downloadOriginal : summary.downloadErrors;
      const download = await getLink(record.id);
      const response = await fetch(download.downloadUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const objectUrl = window.URL.createObjectURL(await response.blob());
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = download.filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      message.error(t("ImportDashboard-DownloadFailed"));
    } finally {
      setDownloadingFiles((previous) => ({ ...previous, [key]: false }));
    }
  };

  const getDurationLabel = (record: JsonlImportRecord) => {
    if (!record.startedAt || !record.finishedAt) return "—";
    const durationSeconds =
      (moment(record.finishedAt).valueOf() - moment(record.startedAt).valueOf()) / 1000;
    if (!Number.isFinite(durationSeconds) || durationSeconds < 0) return "—";
    return durationSeconds < 60
      ? t("ImportDashboard-Seconds", {
          value: numberFormatter.format(Math.round(durationSeconds)),
        })
      : t("ImportDashboard-Minutes", {
          value: numberFormatter.format(Math.round(durationSeconds / 60)),
        });
  };

  const getResultSummaryTooltip = useCallback(
    (record: ImportDashboardRow) => {
      const completedMode = record.resultSummary?.match(
        /^Import completed: \d+ correct, \d+ errors \((batch|bulk) mode\)$/
      )?.[1];

      if (completedMode) {
        return t("ImportDashboard-ResultSummaryCompleted", {
          correct: formatNumber(readCount(record.correct)),
          errors: formatNumber(readCount(record.incorrect)),
          mode: t(
            completedMode === "batch"
              ? "ImportDashboard-BatchMode"
              : "ImportDashboard-BulkMode"
          ),
        });
      }

      return record.resultSummary || record.originalFilename || record.id;
    },
    [t]
  );

  const recentColumns = useMemo<ColumnsType<ImportDashboardRow>>(
    () => [
      {
        title: t("ImportDashboard-File"),
        dataIndex: "originalFilename",
        key: "file",
        fixed: "left",
        width: 240,
        render: (filename: string, record) => (
          <Tooltip title={getResultSummaryTooltip(record)}>
            <Text ellipsis style={{ maxWidth: 220 }}>
              {filename || record.id}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: t("ImportDashboard-Type"),
        dataIndex: "sourceTitleKey",
        key: "source",
        width: 190,
        render: (key: string) => t(key),
      },
      {
        title: t("ImportDashboard-Status"),
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (status: ImportStatus) => (
          <Tag color={statusColor(status)}>{t(getStatusLabelKey(status))}</Tag>
        ),
      },
      {
        title: t("ImportDashboard-Date"),
        key: "startedAt",
        width: 160,
        render: (_, record) => formatDate(getRecordDate(record)),
      },
      {
        title: t("ImportDashboard-Progress"),
        dataIndex: "percentCompleted",
        key: "progress",
        width: 160,
        responsive: ["md"],
        render: (value?: number) => (
          <Progress percent={Math.round(readCount(value))} size="small" />
        ),
      },
      {
        title: t("ImportDashboard-ProcessedRows"),
        key: "processed",
        align: "right",
        width: 130,
        render: (_, record) =>
          `${formatNumber(readCount(record.processedLines))} / ${formatNumber(
            readCount(record.totalLines)
          )}`,
      },
      {
        title: t("ImportDashboard-CorrectRows"),
        dataIndex: "correct",
        key: "correct",
        align: "right",
        width: 120,
        render: (value: number) => formatNumber(readCount(value)),
      },
      {
        title: t("ImportDashboard-ErrorRows"),
        dataIndex: "incorrect",
        key: "incorrect",
        align: "right",
        width: 120,
        render: (value: number) => formatNumber(readCount(value)),
      },
      {
        title: t("ImportDashboard-Warnings"),
        dataIndex: "warnings",
        key: "warnings",
        align: "right",
        width: 110,
        responsive: ["lg"],
        render: (value: number) => formatNumber(readCount(value)),
      },
      {
        title: t("ImportDashboard-Action"),
        key: "action",
        fixed: "right",
        width: 145,
        render: (_, record) => (
          <Button type="link" onClick={() => navigate(record.sourceRoute)}>
            {t("ImportDashboard-OpenImports")}
          </Button>
        ),
      },
    ],
    [getResultSummaryTooltip, navigate, t]
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <Title level={2}>{t("ImportDashboard-Title")}</Title>
          <Text type="secondary">{t("ImportDashboard-Description")}</Text>
        </div>
        <Button
          icon={<ReloadOutlined />}
          loading={refreshing}
          disabled={!businessUnitSelected}
          onClick={() => void loadDashboard(false)}
        >
          {t("ImportDashboard-Refresh")}
        </Button>
      </div>

      {!businessUnitSelected && (
        <Alert
          type="info"
          showIcon
          message={t("ImportDashboard-SelectBusinessUnit")}
          className={styles.notice}
        />
      )}

      {businessUnitSelected && failedSourcesCount > 0 && !loading && (
        <Alert
          type="warning"
          showIcon
          message={t("ImportDashboard-PartialError", {
            count: failedSourcesCount,
          })}
          className={styles.notice}
        />
      )}

      {loading ? (
        <>
          <Row gutter={[16, 16]} className={styles.processGrid}>
            {visibleSources.map((source) => (
              <Col xs={24} sm={12} xl={8} key={source.key}>
                <Card>
                  <Skeleton active paragraph={{ rows: 5 }} />
                </Card>
              </Col>
            ))}
          </Row>
          <Card className={styles.sectionCard}>
            <Skeleton active paragraph={{ rows: 5 }} />
          </Card>
        </>
      ) : businessUnitSelected ? (
        <>
          <div className={styles.scopeNote}>
            <Text type="secondary">
              {t("ImportDashboard-RecentScope", { count: RECENT_PER_SOURCE })}
            </Text>
          </div>

          <Card title={t("ImportDashboard-ByTypeTitle")} className={styles.sectionCard}>
            <Row gutter={[16, 16]} align="top">
              {summaries.map((summary) => {
                const latest = summary.records
                  .slice()
                  .sort((left, right) => {
                    const leftTime = getRecordDate(left);
                    const rightTime = getRecordDate(right);
                    return (
                      (rightTime ? moment(rightTime).valueOf() : 0) -
                      (leftTime ? moment(leftTime).valueOf() : 0)
                    );
                  })[0];
                const canDownloadErrors =
                  latest != null &&
                  readCount(latest.incorrect) > 0 &&
                  ["COMPLETED", "ERROR", "CANCELLED"].includes(latest.status);
                const expanded = Boolean(expandedSources[summary.key]);
                const detailsId = `import-process-details-${summary.key}`;
                const toggleExpanded = () =>
                  setExpandedSources((current) => ({
                    ...current,
                    [summary.key]: !current[summary.key],
                  }));

                return (
                  <Col xs={24} sm={12} xl={8} key={summary.key}>
                    <Card
                      size="small"
                      className={`${styles.processCard} ${
                        expanded ? styles.processCardExpanded : styles.processCardCollapsed
                      }`}
                      title={
                        <Button
                          type="text"
                          className={styles.processCardTitle}
                          aria-expanded={expanded}
                          aria-controls={latest && !summary.failed ? detailsId : undefined}
                          aria-label={t(
                            expanded
                              ? "ImportDashboard-CollapseCard"
                              : "ImportDashboard-ExpandCard",
                            { type: t(summary.titleKey) }
                          )}
                          onClick={toggleExpanded}
                        >
                          <span>{t(summary.titleKey)}</span>
                          <DownOutlined rotate={expanded ? 180 : 0} />
                        </Button>
                      }
                      extra={
                        summary.failed ? (
                          <Tag color="error">{t("ImportDashboard-SourceUnavailable")}</Tag>
                        ) : (
                          <Text type="secondary">
                            {t("ImportDashboard-ProcessCount", {
                              value: formatNumber(summary.totalElements),
                            })}
                          </Text>
                        )
                      }
                    >
                      {summary.failed ? (
                        <Text type="secondary">{t("ImportDashboard-SourceUnavailable")}</Text>
                      ) : latest ? (
                        <>
                          {!expanded && (
                            <div className={styles.compactProcess}>
                              <div className={styles.compactProcessHeading}>
                                <Tooltip title={latest.originalFilename || latest.id}>
                                  <Text ellipsis className={styles.compactFilename}>
                                    {latest.originalFilename || latest.id}
                                  </Text>
                                </Tooltip>
                                <Tag color={statusColor(latest.status)}>
                                  {t(getStatusLabelKey(latest.status))}
                                </Tag>
                              </div>
                              <Text type="secondary" className={styles.compactDate}>
                                {formatDate(getRecordDate(latest))}
                              </Text>
                            </div>
                          )}
                          <div id={detailsId} hidden={!expanded}>
                            {expanded && (
                              <>
                                <div className={styles.latestProcessHeading}>
                                  <div className={styles.latestFile}>
                                    <Text type="secondary">
                                      {t("ImportDashboard-LastProcess")}
                                    </Text>
                                    <Tooltip title={latest.originalFilename || latest.id}>
                                      <Text strong ellipsis className={styles.latestFilename}>
                                        {latest.originalFilename || latest.id}
                                      </Text>
                                    </Tooltip>
                                  </div>
                                  <Tag color={statusColor(latest.status)}>
                                    {t(getStatusLabelKey(latest.status))}
                                  </Tag>
                                </div>
                                <Text type="secondary" className={styles.latestDate}>
                                  {t("ImportDashboard-LastDate")}:{" "}
                                  {formatDate(getRecordDate(latest))}
                                </Text>
                                <Progress
                                  percent={Math.round(readCount(latest.percentCompleted))}
                                  size="small"
                                />
                                <Row gutter={[12, 12]} className={styles.processStats}>
                                  <Col span={12}>
                                    <Statistic
                                      title={t("ImportDashboard-CorrectRows")}
                                      value={readCount(latest.correct)}
                                      valueStyle={{ color: "#389e0d" }}
                                      formatter={(value) => formatNumber(Number(value || 0))}
                                    />
                                  </Col>
                                  <Col span={12}>
                                    <Statistic
                                      title={t("ImportDashboard-ErrorRows")}
                                      value={readCount(latest.incorrect)}
                                      valueStyle={{
                                        color: readCount(latest.incorrect) ? "#cf1322" : undefined,
                                      }}
                                      formatter={(value) => formatNumber(Number(value || 0))}
                                    />
                                  </Col>
                                  <Col span={12}>
                                    <Statistic
                                      title={t("ImportDashboard-ProcessedRows")}
                                      value={`${formatNumber(readCount(latest.processedLines))} / ${formatNumber(readCount(latest.totalLines))}`}
                                    />
                                  </Col>
                                  <Col span={12}>
                                    <Statistic
                                      title={t("ImportDashboard-Warnings")}
                                      value={readCount(latest.warnings)}
                                      formatter={(value) => formatNumber(Number(value || 0))}
                                    />
                                  </Col>
                                </Row>
                                <div className={styles.processMeta}>
                                  <Text type="secondary">
                                    {t("ImportDashboard-LastDuration")}: {getDurationLabel(latest)}
                                  </Text>
                                </div>
                                <Space wrap className={styles.downloadActions}>
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={() => setViewerSelection({ summary, record: latest })}
                                  >
                                    {t("ImportDashboard-VisualizeFile")}
                                  </Button>
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    loading={downloadingFiles[`original-${latest.id}`]}
                                    onClick={() =>
                                      void downloadImportFile("original", summary, latest)
                                    }
                                  >
                                    {t("ImportDashboard-DownloadOriginal")}
                                  </Button>
                                  {canDownloadErrors && (
                                    <Button
                                      type="link"
                                      size="small"
                                      icon={<DownloadOutlined />}
                                      loading={downloadingFiles[`errors-${latest.id}`]}
                                      onClick={() =>
                                        void downloadImportFile("errors", summary, latest)
                                      }
                                    >
                                      {t("ImportDashboard-DownloadErrors")}
                                    </Button>
                                  )}
                                </Space>
                              </>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className={styles.emptyProcess}>
                          <Text type="secondary">{t("ImportDashboard-NoProcess")}</Text>
                        </div>
                      )}
                      {expanded && (
                        <Button
                          type="link"
                          className={styles.processAction}
                          onClick={() => navigate(summary.route)}
                        >
                          {t("ImportDashboard-OpenImports")}
                        </Button>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card>

          <Card
            title={t("ImportDashboard-RecentTitle")}
            className={styles.sectionCard}
            extra={
              <Space wrap>
                <Input.Search
                  allowClear
                  placeholder={t("ImportDashboard-Search")}
                  onChange={(event) => setSearch(event.target.value)}
                  className={styles.searchInput}
                />
                <Select
                  allowClear
                  placeholder={t("ImportDashboard-FilterType")}
                  className={styles.filterSelect}
                  value={sourceFilter}
                  onChange={setSourceFilter}
                  options={summaries.map((summary) => ({
                    label: t(summary.titleKey),
                    value: summary.key,
                  }))}
                />
                <Select
                  allowClear
                  placeholder={t("ImportDashboard-FilterStatus")}
                  className={styles.filterSelect}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    "PENDING",
                    "PROCESSING",
                    "COMPLETED",
                    "ERROR",
                    "CANCELLED",
                  ].map((status) => ({
                    label: t(getStatusLabelKey(status)),
                    value: status,
                  }))}
                />
              </Space>
            }
          >
            <Table<ImportDashboardRow>
              rowKey="rowKey"
              columns={recentColumns}
              dataSource={filteredRows}
              locale={{ emptyText: t("ImportDashboard-NoImports") }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50],
                showTotal: (total) =>
                  t("ImportDashboard-TableTotal", { count: total }),
              }}
              scroll={{ x: 1480 }}
              size="middle"
            />
          </Card>
        </>
      ) : null}
      {viewerSelection && viewerFetchPage && (
        <JsonlViewerModal
          visible
          onClose={() => setViewerSelection(null)}
          title={t("ImportDashboard-VisualizeTitle", {
            filename:
              viewerSelection.record.originalFilename || viewerSelection.record.id,
          })}
          fetchPage={viewerFetchPage}
        />
      )}
    </div>
  );
};

export default ImportDashboard;
